import toast from 'react-hot-toast';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import {
  setLoadingMessage,
  setPolyCount,
} from '../../redux/Slice/globalStates';
import {
  add_new_assets,
  addImage,
  addSceneImage,
  selected_object,
  set_is_glb_image_editoropen,
  setImageScales,
  setPushIntersectedObject,
  update_assets_data,
} from '../../redux/Slice/gridSlice';
import store from '../../redux/store';
import { getNextCopyName, buildTempFileName } from '../../utils/NamingUtils';
import { copyPhysicsToDuplicate } from '../../utils/Physics/PhysicsUtils';
import {
  UpdateMaterial,
  materialSettingsToThreeProps,
} from './materialFunction';
import {
  captureDiscreteHistoryBefore,
  recordDiscreteHistoryAfter,
} from '../../utils/gridHistoryDiscrete';
import { suppressGridHistory } from '../../utils/gridHistorySuppress';
import { applyStoredPivotToObject } from '../../utils/ThreeJsUtils/pivotEditor';



export function handleImageUpload(
  e,
  { Three, scene, transformControls, selectedObject, setPolyCyount },
  setPendingUploads
) {
  if (!e.target.files || e.target.files.length === 0) {
    return;
  }

  const file = e.target.files[0];
  const maxSize = 2 * 1024 * 1024; // 2MB size limit

  if (file.size > maxSize) {
    toast.error(
      () => (
        <div className="flex justify-between items-center">
          <span>File size should not exceed 2MB.</span>
        </div>
      ),
      {
        duration: 3000,
        style: {
          background: '#1d1e21',
          color: '#fff',
        },
      }
    );
    return;
  }

  selectedObject = null;
  store.dispatch(selected_object(null));
  transformControls.detach();
  store.dispatch(setLoadingMessage(true));
  const objectURL = URL.createObjectURL(file);
  const tempId = `temp-${Date.now()}-${file.name}`; // Unique ID for scene lookups

  new Three.TextureLoader().load(
    objectURL,
    function (texture) {
      const material = new Three.MeshStandardMaterial({
        map: texture,
        side: Three.DoubleSide,
        transparent: true,
      });

      const imageWidth = texture.image.width;
      const imageHeight = texture.image.height;
      const imageAspectRatio = imageWidth / imageHeight;

      let planeWidth, planeHeight;

      if (imageAspectRatio > 1) {
        planeHeight = 1;
        planeWidth = imageWidth / imageHeight;
      } else if (imageAspectRatio < 1) {
        planeWidth = 1;
        planeHeight = imageHeight / imageWidth;
      } else {
        planeWidth = 1;
        planeHeight = 1;
      }

      const geometry = new Three.PlaneGeometry(planeWidth, planeHeight);
      const imageMesh = new Three.Mesh(geometry, material);
      imageMesh.castShadow = true;
      imageMesh.receiveShadow = true;

      if (setPendingUploads) {
        setPendingUploads((prev) => [
          ...prev,
          {
            file: file,
            transform: {
              position_x: 0,
              position_y: 0,
              position_z: 0,
              rotation_x: 0,
              rotation_y: 0,
              rotation_z: 0,
              scale_x: 1,
              scale_y: 1,
              scale_z: 1,
            },
            tempId: tempId,
          },
        ]);
      }

      // ✅ FIXED: Use tempId only for scene lookups, file.name for display
      imageMesh.name = tempId; // For scene.getObjectByName() lookups
      imageMesh._id = tempId; // Unique identifier for tracking
      imageMesh.url = tempId; // For backward compatibility
      imageMesh.objPath = file;
      imageMesh.file_name = file.name; // ✅ Actual filename for display
      imageMesh.deleteType = 'image';
      imageMesh.originalFile = file;
      imageMesh.localFile = file;
      imageMesh.temp_file_name = tempId; // ✅ Keep temp ID separate

      // ✅ Store file_name in userData as well
      imageMesh.userData = {
        file_name: file.name, // ✅ For display purposes
        material_settings: {
          albedo: '#FFFFFF',
          side: 'Double',
          transparency: true,
          opacity: 1,
          metalicType: false,
          emissionType: false,
          added: true,
        },
      };

      store.dispatch(setImageScales({ x: planeWidth, y: planeHeight }));
      store.dispatch(setPushIntersectedObject(imageMesh));

      const beforeSnapshot = captureDiscreteHistoryBefore(scene);
      const endSuppress = suppressGridHistory();
      try {
        scene.add(imageMesh);
        store.dispatch(addSceneImage(imageMesh));
        store.dispatch(addImage({ url: objectURL, name: file.name }));
        store.dispatch(add_new_assets({ type: 'image', data: imageMesh }));
      } finally {
        endSuppress();
        recordDiscreteHistoryAfter(beforeSnapshot, scene, 'add image');
      }

      setPolyCount((prev) => prev + 1);
      selectObjectInScene(tempId);
      toast.success('Image added to scene successfully');
      store.dispatch(set_is_glb_image_editoropen(true));
      store.dispatch(setLoadingMessage(false));
    },
    undefined,
    function (error) {
      console.error(error);
      toast.error('Failed to load image file');
      store.dispatch(setLoadingMessage(false));
    }
  );
}

export function DownloadImage(
  imageData,
  {
    Three,

    scene,

    selectedObject,
    setPolyCount,
  }
) {
  imageData.forEach((image, index) => {
    var imagePath = image.asset_file_path;
    var position_x = image.position_x;
    var position_y = image.position_y;
    var position_z = image.position_z;

    var rotation_x = image.rotation_x;
    var rotation_y = image.rotation_y;
    var rotation_z = image.rotation_z;

    var scale_x = image.scale_x;
    var scale_y = image.scale_y;
    var scale_z = image.scale_z;

    // imageUrls.push(image.asset_file_path); // redux done
    // imageFileName.push(image.asset_file_name); // redux done

    // redux
    store.dispatch(
      addImage({ url: image.asset_file_path, name: image.asset_file_name })
    );
    new Three.TextureLoader().load(imagePath, function (texture) {
      const settings = image.material_settings || {};
      const surfaceProps = materialSettingsToThreeProps(settings, Three);

      const materialProps = {
        map: texture,
        ...surfaceProps,
        color: settings.albedo
          ? new Three.Color(settings.albedo)
          : new Three.Color('#ffffff'),
        metalness:
          settings.metalness !== undefined
            ? parseFloat(settings.metalness) || 0
            : 0,
        roughness:
          settings.roughness !== undefined
            ? parseFloat(settings.roughness) || 1
            : 1,
        emissive:
          settings.emission === 'Color/Texture'
            ? new Three.Color(settings.albedo || '#000000')
            : new Three.Color('#000000'),
        receiveShadow: true,
        castShadow: true,
      };

      // You can add support for normal/emissive/metallic maps here if you start loading them from URLs
      const materialImage = new Three.MeshStandardMaterial(materialProps);

      const imageWidth = texture.image.width;
      const imageHeight = texture.image.height;
      const imageAspectRatio = imageWidth / imageHeight;
      let planeWidth, planeHeight;

      if (imageAspectRatio > 1) {
        planeHeight = 1;
        planeWidth = imageWidth / imageHeight;
      } else if (imageAspectRatio < 1) {
        planeWidth = 1;
        planeHeight = imageHeight / imageWidth;
      } else {
        planeWidth = 1;
        planeHeight = 1;
      }

      // pushSceneSnapshot(scene);

      let imagePlane = new Three.PlaneGeometry(planeWidth, planeHeight);
      const imageMesh = new Three.Mesh(imagePlane, materialImage);
      imageMesh.castShadow = true;
      imageMesh.receiveShadow = true;
      imageMesh.name = image._id;
      imageMesh.url = image._id;
      imageMesh.file_name = image.file_name;
      imageMesh.deleteType = 'image';
      imageMesh.events = image.events || [];

      imageMesh.rotation.eulerOrder = 'XYZ';
      imageMesh.rotation.order = 'XYZ';

      imageMesh.position.set(position_x, position_y, position_z);
      imageMesh.rotation.set(
        Three.MathUtils.degToRad(rotation_x),
        Three.MathUtils.degToRad(rotation_y),
        Three.MathUtils.degToRad(rotation_z)
      );

      imageMesh.scale.set(scale_x / planeWidth, scale_y / planeHeight, scale_z);
      imageMesh.updateMatrix();
      // imageScaleX.push(planeWidth);
      // imageScaleY.push(planeHeight);
      store.dispatch(setImageScales({ x: planeWidth, y: planeHeight }));
      imageMesh.userData = imageMesh.userData || {};
      imageMesh.userData.file_name = image.file_name;
      imageMesh.userData.material_settings = image.material_settings;
      imageMesh.Events = image.events || [];
      imageMesh.temp_file_name = image.temp_file_name;
      applyStoredPivotToObject(imageMesh, image, {
        projectId:
          typeof window !== 'undefined'
            ? window.location?.pathname?.split('/').filter(Boolean).pop()
            : null,
        cacheKeys: [
          image._id,
          image.id,
          image.name,
          image.temp_file_name,
          image.file_name,
          image.url,
        ],
      });
      const beforeSnapshot = captureDiscreteHistoryBefore(scene);
      const endSuppress = suppressGridHistory();
      try {
        scene.add(imageMesh);
      // Handle Unlit shader with emission texture before calling UpdateMaterial
      if (
        image.material_settings?.shader === 'Unlit' &&
        image.material_settings?.textureImage
      ) {
        // For Unlit shaders with emission textures, we need to handle this specially
        // to avoid the uniforms.emissiveMap error
        const processedSettings = { ...image.material_settings };

        // Apply material settings first (this will preserve textureImage in userData)
        UpdateMaterial({ object: imageMesh, addData: processedSettings });

        // Then manually apply the emission texture as the main map for Unlit materials
        if (
          imageMesh.material.isMeshBasicMaterial &&
          processedSettings.textureImage
        ) {
          // Load the emission texture as the main map for Unlit materials
          const textureLoader = new Three.TextureLoader();
          textureLoader.load(
            processedSettings.textureImage,
            (emissionTexture) => {
              imageMesh.material.map = emissionTexture;
              imageMesh.material.needsUpdate = true;
            }
          );
        }
      } else {
        UpdateMaterial({ object: imageMesh, addData: image.material_settings });
      }

      selectedObject = imageMesh;
      // sceneImages.push(imageMesh); // redux done

      setPolyCount((prevCount) => prevCount + 1);
      // allIntersectedObjects.push(imageMesh);
      store.dispatch(setPushIntersectedObject(imageMesh));

      store.dispatch(selected_object(imageMesh));
      store.dispatch(addSceneImage(imageMesh));
      } finally {
        endSuppress();
        recordDiscreteHistoryAfter(beforeSnapshot, scene, 'add image');
      }

      selectObjectInScene(image._id);
    });
  });
}

export function duplicateImageAsset(
  item, // Now receiving the full item object
  { Three, scene, transformControls, selectedObject, setPolyCount },
  setPendingUploads, // For handling pending uploads if needed
  originalMesh = null, // Optional: pass the original mesh to copy its properties
  options = {}
) {
  const attachToParent = options.attachToParent || null;
  const localTransform = options.localTransform || null;

  if (!item) {
    toast.error(
      'Valid item with file path and name are required for duplication'
    );
    return;
  }

  // Hierarchy child duplicate: clone live mesh under the new parent (sync).
  if (attachToParent && originalMesh?.isObject3D) {
    const baseName = (
      originalMesh.file_name ||
      item.file_name ||
      originalMesh.name ||
      'Image'
    ).replace(/\.[^/.]+$/, '');
    const ext =
      (item.file_name && item.file_name.match(/\.[^/.]+$/)?.[0]) ||
      originalMesh.file_name?.match(/\.[^/.]+$/)?.[0] ||
      '';
    const duplicateFileName = getNextCopyName(scene, baseName) + ext;
    const tempId = buildTempFileName(duplicateFileName);
    const cloned = originalMesh.clone(true);
    cloned.uuid = Three.MathUtils.generateUUID();
    if (cloned.material) {
      cloned.material = Array.isArray(cloned.material)
        ? cloned.material.map((m) => m?.clone?.() || m)
        : cloned.material.clone?.() || cloned.material;
    }
    cloned.name = tempId;
    cloned.url = tempId;
    cloned._id = tempId;
    cloned.temp_file_name = tempId;
    cloned.file_name = duplicateFileName;
    cloned.deleteType = 'image';
    attachToParent.add(cloned);
    if (localTransform) {
      cloned.position.copy(localTransform.position);
      cloned.rotation.copy(localTransform.rotation);
      cloned.scale.copy(localTransform.scale);
    } else {
      cloned.position.copy(originalMesh.position);
      cloned.rotation.copy(originalMesh.rotation);
      cloned.scale.copy(originalMesh.scale);
    }
    cloned.updateMatrixWorld(true);
    store.dispatch(setPushIntersectedObject(cloned));
    store.dispatch(addSceneImage(cloned));
    copyPhysicsToDuplicate(originalMesh, cloned);
    const parentId =
      attachToParent._id || attachToParent.name || attachToParent.url;
    store.dispatch(
      add_new_assets({
        type: 'image',
        data: { ...cloned, parent: parentId },
      })
    );
    const list = store.getState().assets_object?.assets_data?.image || [];
    const updated = list.map((entry) => {
      const id = entry._id || entry.id || entry.name || entry.url;
      if (id !== tempId) return entry;
      return { ...entry, parent: parentId };
    });
    if (!updated.some((e) => (e._id || e.id || e.name || e.url) === tempId)) {
      updated.push({
        _id: tempId,
        id: tempId,
        name: tempId,
        file_name: duplicateFileName,
        parent: parentId,
        deleteType: 'image',
      });
    }
    store.dispatch(update_assets_data({ type: 'image', data: updated }));
    return cloned;
  }

  // When item has localFile but no asset_file_path/file_name, use handleImageUpload logic
  if (item.localFile && (!item.asset_file_path || !item.file_name)) {
    const file = item.localFile;
    const maxSize = 2 * 1024 * 1024; // 2MB size limit
    if (file.size > maxSize) {
      toast.error(
        () => (
          <div className="flex justify-between items-center">
            <span>File size should not exceed 2MB.</span>
          </div>
        ),
        {
          duration: 3000,
          style: {
            background: '#1d1e21',
            color: '#fff',
          },
        }
      );
      return;
    }
    selectedObject = null;
    store.dispatch(selected_object(null));
    transformControls.detach();
    store.dispatch(setLoadingMessage(true));
    const objectURL = URL.createObjectURL(file);
    // Use item.file_name if available (handles renamed images), otherwise fall back to file.name
    const nameToUse = item.file_name || item.asset_file_name || file.name;
    const baseName = nameToUse.replace(/\.[^/.]+$/, '');
    // Get extension from item.file_name first, then fall back to file extension
    const ext =
      (item.file_name && item.file_name.match(/\.[^/.]+$/)?.[0]) ||
      nameToUse.match(/\.[^/.]+$/)?.[0] ||
      file.name.match(/\.[^/.]+$/)?.[0] ||
      '';
    const duplicateFileName = getNextCopyName(scene, baseName) + ext;
    const tempId = buildTempFileName(duplicateFileName);

    new Three.TextureLoader().load(
      objectURL,
      function (texture) {
        const material = new Three.MeshStandardMaterial({
          map: texture,
          side: Three.DoubleSide,
          transparent: true,
        });

        const imageWidth = texture.image.width;
        const imageHeight = texture.image.height;
        const imageAspectRatio = imageWidth / imageHeight;

        let planeWidth, planeHeight;

        if (imageAspectRatio > 1) {
          planeHeight = 1;
          planeWidth = imageWidth / imageHeight;
        } else if (imageAspectRatio < 1) {
          planeWidth = 1;
          planeHeight = imageHeight / imageWidth;
        } else {
          planeWidth = 1;
          planeHeight = 1;
        }

        const geometry = new Three.PlaneGeometry(planeWidth, planeHeight);
        const imageMesh = new Three.Mesh(geometry, material);
        imageMesh.castShadow = true;
        imageMesh.receiveShadow = true;

        const sourceMesh = originalMesh || scene.getObjectByName(item?._id || item?.id || item?.url || item?.name);

        if (setPendingUploads) {
          setPendingUploads((prev) => [
            ...prev,
            {
              file: file,
              transform: {
                position_x: sourceMesh?.position?.x || 0,
                position_y: sourceMesh?.position?.y || 0,
                position_z: sourceMesh?.position?.z || 0,
                rotation_x: Three.MathUtils.radToDeg(sourceMesh?.rotation?.x || 0),
                rotation_y: Three.MathUtils.radToDeg(sourceMesh?.rotation?.y || 0),
                rotation_z: Three.MathUtils.radToDeg(sourceMesh?.rotation?.z || 0),
                scale_x: sourceMesh?.scale?.x || 1,
                scale_y: sourceMesh?.scale?.y || 1,
                scale_z: sourceMesh?.scale?.z || 1,
              },
              tempId: tempId,
            },
          ]);
        }

        imageMesh.position.set(
          sourceMesh?.position?.x || 0,
          sourceMesh?.position?.y || 0,
          sourceMesh?.position?.z || 0
        );
        imageMesh.rotation.copy(sourceMesh?.rotation || new Three.Euler());
        imageMesh.scale.copy(sourceMesh?.scale || new Three.Vector3(1, 1, 1));

        imageMesh.name = tempId;
        imageMesh._id = tempId;
        imageMesh.url = tempId;
        imageMesh.objPath = file;
        imageMesh.file_name = duplicateFileName;
        imageMesh.deleteType = 'image';
        imageMesh.originalFile = file;
        imageMesh.localFile = file;
        imageMesh.temp_file_name = tempId;

        imageMesh.userData = {
          file_name: duplicateFileName,
          material_settings: {
            albedo: '#FFFFFF',
            metalicType: false,
            emissionType: false,
            added: true,
          },
        };

        store.dispatch(setImageScales({ x: planeWidth, y: planeHeight }));
        store.dispatch(setPushIntersectedObject(imageMesh));

        scene.add(imageMesh);
        store.dispatch(addSceneImage(imageMesh));
        copyPhysicsToDuplicate(sourceMesh || originalMesh || item, imageMesh);
        store.dispatch(addImage({ url: objectURL, name: duplicateFileName }));
        store.dispatch(add_new_assets({ type: 'image', data: imageMesh }));

        setPolyCount((prev) => prev + 1);
        selectObjectInScene(tempId);
        toast.success('Image added to scene successfully');

        store.dispatch(set_is_glb_image_editoropen(true));
        store.dispatch(setLoadingMessage(false));
      },
      undefined,
      function (error) {
        console.error(error);
        toast.error('Failed to load image file');
        store.dispatch(setLoadingMessage(false));
      }
    );
    return;
  }

  if (!item.asset_file_path || !item.file_name) {
    toast.error(
      'Valid item with file path and name are required for duplication'
    );
    return;
  }

  // Clear current selection
  selectedObject = null;
  store.dispatch(selected_object(null));
  transformControls.detach();

  // Set loading state
  store.dispatch(setLoadingMessage(true));

  // Generate unique properties for duplicate
  const baseName = item.file_name.replace(/\.[^/.]+$/, ''); // strip extension
  const duplicateFileName =
    getNextCopyName(scene, baseName) +
    (item.file_name.match(/\.[^/.]+$/)?.[0] || '');

  const tempId = buildTempFileName(duplicateFileName);
  const duplicateImageCode = `mesh_${Math.random().toString(36).substr(2, 6)}`;

  new Three.TextureLoader().load(
    item.asset_file_path,
    function (texture) {
      // Create material using item's material_settings
      const materialSettings = item.material_settings || {};
      const surfaceProps = materialSettingsToThreeProps(materialSettings, Three);

      let materialProps = {
        map: texture,
        ...surfaceProps,
        color: materialSettings.albedo
          ? new Three.Color(materialSettings.albedo)
          : new Three.Color('#ffffff'),
        metalness:
          materialSettings.metalness !== undefined
            ? parseFloat(materialSettings.metalness) || 0
            : 0,
        roughness:
          materialSettings.roughness !== undefined
            ? parseFloat(materialSettings.roughness) || 1
            : 1,
        emissive:
          materialSettings.emission === 'Color/Texture'
            ? new Three.Color(materialSettings.albedo || '#000000')
            : new Three.Color('#000000'),
        receiveShadow: true,
        castShadow: false,
      };

      // If original mesh is provided, copy its material settings
      if (
        originalMesh &&
        originalMesh.userData &&
        originalMesh.userData.material_settings
      ) {
        const settings = originalMesh.userData.material_settings;
        materialProps = {
          ...materialProps,
          ...materialSettingsToThreeProps(settings, Three),
          color: settings.albedo
            ? new Three.Color(settings.albedo)
            : new Three.Color('#ffffff'),
          metalness:
            settings.metalness !== undefined
              ? parseFloat(settings.metalness) || 0
              : 0,
          roughness:
            settings.roughness !== undefined
              ? parseFloat(settings.roughness) || 1
              : 1,
          emissive:
            settings.emission === 'Color/Texture'
              ? new Three.Color(settings.albedo || '#000000')
              : new Three.Color('#000000'),
          receiveShadow: true,
          castShadow: true,
        };
      }

      const material =
        originalMesh &&
        originalMesh.material instanceof Three.MeshStandardMaterial
          ? new Three.MeshStandardMaterial(materialProps)
          : new Three.MeshStandardMaterial(materialProps);

      // Calculate plane dimensions
      const imageWidth = texture.image.width;
      const imageHeight = texture.image.height;
      const imageAspectRatio = imageWidth / imageHeight;

      let planeWidth, planeHeight;

      if (imageAspectRatio > 1) {
        planeHeight = 1;
        planeWidth = imageWidth / imageHeight;
      } else if (imageAspectRatio < 1) {
        planeWidth = 1;
        planeHeight = imageHeight / imageWidth;
      } else {
        planeWidth = 1;
        planeHeight = 1;
      }

      const geometry = new Three.PlaneGeometry(planeWidth, planeHeight);
      const duplicatedMesh = new Three.Mesh(geometry, material);
      duplicatedMesh.castShadow = true;
      duplicatedMesh.receiveShadow = true;

      // Set all properties from the original item
      duplicatedMesh.name = tempId;
      duplicatedMesh.url = tempId;
      duplicatedMesh.deleteType = 'image';
      duplicatedMesh.asset_file_path = item.asset_file_path;

      // const tempMesh=scene.getObjectByName(item._id);

      // Apply transform properties from item with slight offset to avoid overlap
      // Prioritize scene object (tempMesh or originalMesh) for current transform
      const sourceMesh = originalMesh || scene.getObjectByName(item?._id || item?.id || item?.url || item?.name);

      if (sourceMesh) {
        duplicatedMesh.position.set(
          sourceMesh.position.x,
          sourceMesh.position.y,
          sourceMesh.position.z
        );
        duplicatedMesh.rotation.copy(sourceMesh.rotation);
        duplicatedMesh.scale.copy(sourceMesh.scale);
      } else {
        duplicatedMesh.position.set(
          item.position_x || 0,
          item.position_y || 0,
          item.position_z || 0
        );
        duplicatedMesh.rotation.set(
          Three.MathUtils.degToRad(item.rotation_x || 0),
          Three.MathUtils.degToRad(item.rotation_y || 0),
          Three.MathUtils.degToRad(item.rotation_z || 0)
        );
        duplicatedMesh.scale.set(
          item.scale_x || 1,
          item.scale_y || 1,
          item.scale_z || 1
        );
      }
      duplicatedMesh.file_name = duplicateFileName;

      // Store all item properties in userData
      duplicatedMesh.userData = {
        material_settings: { ...materialSettings },
        project: item.project,
        user: item.user,
        asset_file_name: item.asset_file_name,
        file_name: duplicateFileName,
        asset_file_type: item.asset_file_type,
        asset_file_path: item.asset_file_path,
        published: item.published,
        asset_thumbnail: item.asset_thumbnail,
        position_x: duplicatedMesh.position.x,
        position_y: duplicatedMesh.position.y,
        position_z: duplicatedMesh.position.z,
        scale_x: duplicatedMesh.scale.x,
        scale_y: duplicatedMesh.scale.y,
        scale_z: duplicatedMesh.scale.z,
        rotation_x: item.rotation_x || 0,
        rotation_y: item.rotation_y || 0,
        rotation_z: item.rotation_z || 0,
        events: [...(item.events || [])], // Copy events array
        saved: false, // Mark as unsaved since it's a duplicate
        image_code: duplicateImageCode,
        originalId: item._id || item.id, // Reference to original item
        isDuplicate: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Handle pending uploads if needed
      if (setPendingUploads && originalMesh && originalMesh.originalFile) {
        setPendingUploads((prev) => [
          ...prev,
          {
            file: originalMesh.originalFile,
            transform: {
              position_x: duplicatedMesh.position.x,
              position_y: duplicatedMesh.position.y,
              position_z: duplicatedMesh.position.z,
              rotation_x: item.rotation_x || 0,
              rotation_y: item.rotation_y || 0,
              rotation_z: item.rotation_z || 0,
              scale_x: duplicatedMesh.scale.x,
              scale_y: duplicatedMesh.scale.y,
              scale_z: duplicatedMesh.scale.z,
            },
            tempId: tempId,
            itemData: duplicatedMesh.userData, // Include all item data
          },
        ]);
      }

      // Update Redux store
      store.dispatch(setImageScales({ x: planeWidth, y: planeHeight }));
      store.dispatch(setPushIntersectedObject(duplicatedMesh));

      // Add to scene
      scene.add(duplicatedMesh);

      // Update counters and select the new object
      setPolyCount((prev) => prev + 1);
      selectObjectInScene(tempId);

      // // Show success message
      // toast.success(`Image duplicated successfully as "${duplicateFileName}"`);

      const displayName =
        tempId.length > 30 ? `${tempId.substring(0, 30)}...` : tempId;
      toast.success(`Particle duplicated as "${displayName}"`);
      // Update Redux store with new asset - include all original properties
      const duplicateItemData = {
        ...item,
        _id: tempId,
        id: tempId,
        temp_file_name: tempId,
        file_name: duplicateFileName,
        image_code: duplicateImageCode,
        position_x: duplicatedMesh.position.x,
        position_y: duplicatedMesh.position.y,
        position_z: duplicatedMesh.position.z,
        parent: undefined,
        saved: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        originalId: item._id || item.id,
        isDuplicate: true,
      };

      store.dispatch(addSceneImage(duplicatedMesh));
      copyPhysicsToDuplicate(sourceMesh || originalMesh || item, duplicatedMesh);
      store.dispatch(
        addImage({
          url: item.asset_file_path,
          name: duplicateFileName,
          ...duplicateItemData,
        })
      );
      store.dispatch(
        add_new_assets({
          type: 'image',
          data: duplicatedMesh,
          itemData: duplicateItemData,
        })
      );
      store.dispatch(set_is_glb_image_editoropen(true));
      store.dispatch(selected_object(duplicatedMesh));
      store.dispatch(setLoadingMessage(false));
    },
    undefined,
    function (error) {
      console.error('Error duplicating image:', error);
      toast.error('Failed to duplicate image asset');
      store.dispatch(setLoadingMessage(false));
    }
  );
}
